<script setup lang="ts">
import { computed } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import Divider from '@/components/common/divider.vue';
import InsightAdjustButton from '@/components/home/insights/insight-adjust-button.vue';
import InsightProfileModal from '@/components/home/insights/insight-profile-modal.vue';
import InsightRegenerateBanner from '@/components/home/insights/insight-regenerate-banner.vue';
import InsightsGenerationError from '@/components/home/insights/insights-generation-error.vue';
import InsightsProfileReview from '@/components/home/insights/insights-profile-review.vue';
import InsightsTabs from '@/components/home/insights/insights-tabs.vue';
import { useInsightsContainer } from '@/components/home/insights/use-insights-container';
import InsightTopicsDescription from '@/components/insights/insight-topics-description.vue';
import { formatDate } from '@/utils/formatters';
import InsightsProfile from './insights-profile.vue';
import { featureFlags } from '@/featureFlags';

const {
  hasLoaded,
  userProfileEnabled,
  hasProfile,
  personalSurveyProfile,
  canAdjustHints,
  profileModalVariant,
  isProfileModalOpen,
  isSavingProfile,
  hasProfileSaveError,
  saveProfile,
  onSuggestionApply,
  onSuggestionRefresh,
  hasSetupPersonalNewsOrFavoriteThemes,
  hasPersonalNewsArticles,
  hasFeaturedArticles,
  payload,
  isGenerating,
  canOpen,
  isOpen,
  isUnviewed,
  isProcessedAndViewed,
  toggleOpen,
  onProfileSaved,
  onRegenerate,
  retry,
  hasGenerationError,
  hasRegenerationError,
  showRegenerateBanner,
  dismiss,
  progress,
  progressBarWidth,
  durationLabel,
  showProgress,
  isComplete,
  errorTitle,
  errorMessage,
} = useInsightsContainer({ pageName: 'home' });

const showsSampleEntry = computed(
  () => featureFlags.ANDEV_10807_INSIGHT_SAMPLE && !hasProfile.value,
);

const dateStr = formatDate(dayjs().toISOString());

const openButtonMessage = computed(() =>
  !hasProfile.value ? '業務情報を登録' : 'ヒントを見てみる',
);

const progressMessage = computed(() =>
  progress.value > 0.8
    ? 'まもなく完了します'
    : progress.value > 0.5
      ? '視点を広げて選んでいます'
      : ` 発想のヒントを生成中（約${durationLabel.value}）`,
);

const handleProfileSave = () => {
  onProfileSaved();
  isOpen.value = false;
};
</script>

<template>
  <div v-if="hasLoaded && userProfileEnabled" class="insights-wrapper">
    <div
      class="insights-container"
      :class="{ unviewed: isUnviewed, 'sample-entry': showsSampleEntry }"
    >
      <div class="title">
        <DgrIcon
          name="tipss"
          :keep-fill="false"
          class="aconnect-home-icon insight-icon"
          :class="{ loading: isGenerating }"
        />
        <div class="c-title-bold--xm">
          発想のヒント<span class="c-title-medium--m beta">(β)</span>
        </div>
        <div v-if="!showsSampleEntry" class="c-text-regular--s insight-date">
          {{ dateStr }}
        </div>
        <div v-if="!showsSampleEntry" class="vertical-divider"></div>
        <InsightTopicsDescription
          :requires-profile="!hasProfile"
          class="description"
          :class="showsSampleEntry ? 'c-text-regular--s' : 'c-text-regular--m'"
        />
        <div v-if="showsSampleEntry" class="actions">
          <RouterLink
            :to="{ name: 'insightsToday' }"
            class="c-btn c-btn--small c-btn--auto c-outlineBtn sample-link"
          >
            <span class="c-title-medium--m">サンプルを見る</span>
          </RouterLink>
          <button
            type="button"
            class="c-btn c-btn--small c-btn--auto c-btn--AnewsPrimary"
            @click="isProfileModalOpen = true"
          >
            業務情報を登録
          </button>
        </div>
        <template v-else-if="!hasProfile || canOpen">
          <button
            v-if="isProcessedAndViewed && !isOpen"
            class="c-title-bold--s open-again-button"
            @click="toggleOpen"
          >
            もう一度見る
          </button>
          <button
            v-else
            class="c-btn c-btn--small c-btn--auto"
            :class="isOpen ? 'c-outlineBtn' : 'c-btn--AnewsPrimary'"
            @click="toggleOpen"
          >
            {{ isOpen ? '閉じる' : openButtonMessage }}
          </button>
        </template>
        <template v-else-if="showProgress">
          <div class="loading-container">
            <div class="c-title-bold--s loading-message">
              {{ progressMessage }}
            </div>
            <div class="progress-wrapper">
              <div class="progress-bar">
                <div class="progress-bar-fill" :class="{ done: isComplete }" />
              </div>
            </div>
          </div>
        </template>
      </div>
      <template v-if="isOpen && !showProgress">
        <InsightsProfile
          v-if="!hasProfile"
          @profile-saved="handleProfileSave"
          :is-always-open="true"
        />
        <InsightsGenerationError
          v-else-if="hasGenerationError"
          :title="errorTitle"
          :message="errorMessage"
          @retry="retry"
        />
        <template v-else>
          <InsightsGenerationError
            v-if="hasRegenerationError"
            title="発想のヒントの再生成に失敗しました"
            :message="errorMessage"
            @retry="retry"
          />
          <InsightRegenerateBanner
            v-else-if="showRegenerateBanner"
            @regenerate="onRegenerate"
            @close="dismiss"
          />
          <div class="saved-link">
            <InsightAdjustButton
              v-if="canAdjustHints"
              @click="isProfileModalOpen = true"
            />
            <RouterLink
              :to="{ name: 'savedInsights' }"
              class="c-btn c-btn--small c-btn--auto c-outlineBtn"
            >
              <DgrIcon
                size="small"
                name="bookmark-fill"
                :keep-fill="false"
                class="saved-link-icon"
              />
              <div>保存済み一覧</div>
            </RouterLink>
          </div>
          <Divider class="full-bleed" margin="0 -12px 0 -12px" />
          <InsightsTabs
            :payload="payload"
            :has-setup-personal-news-or-favorite-themes="
              hasSetupPersonalNewsOrFavoriteThemes
            "
            :has-personal-news-articles="hasPersonalNewsArticles"
            :has-featured-articles="hasFeaturedArticles"
          />
          <Divider class="full-bleed" margin="0 -12px 0 -12px" />
          <InsightsProfileReview />
        </template>
      </template>
    </div>
  </div>
  <InsightProfileModal
    v-if="personalSurveyProfile"
    v-model:open="isProfileModalOpen"
    :variant="profileModalVariant"
    :profile="personalSurveyProfile"
    :is-submitting="isSavingProfile"
    :has-save-error="hasProfileSaveError"
    @save="saveProfile"
    @suggestion-apply="onSuggestionApply"
    @suggestion-refresh="onSuggestionRefresh"
  />
</template>
<style lang="scss" scoped>
.insights-wrapper {
  box-sizing: border-box;
}
.insights-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  background-color: #fff;
  border: 1px solid $color-gray400;

  &.unviewed {
    background-color: $color-green100;
    border: 1px solid $color-green600;
  }

  &.sample-entry {
    padding: 16px;

    .title .description {
      color: $color-gray800;
    }
  }

  .insight-icon {
    color: $color-green400;

    &.loading {
      transition: transform 0.2s ease;
      transform-origin: 50% 62%;
      animation: 3.4s ease-in-out infinite ideaGlow;
    }
  }

  .title {
    display: flex;
    gap: 8px;
    align-items: center;

    // 説明文の flex-basis を 0 にして余った幅だけを渡すと、
    // タイトルや日付、ボタンは自然幅のまま縮まず、折り返すのは説明文だけになる。
    .description {
      flex: 1;
      min-width: 0;
    }

    .beta {
      color: $color-gray800;
    }

    .insight-date {
      color: $color-gray800;
    }

    .vertical-divider {
      width: 1px;
      height: 22px;
      background: $color-gray600;
    }

    .actions {
      display: flex;
      flex-shrink: 0;
      gap: 8px;
      margin-left: auto;
    }

    .sample-link {
      color: $color-gray1000;
      background-color: $color-white;
    }

    > button {
      margin-left: auto;

      &.open-again-button {
        height: 32px;
        padding: 0 16px;
        color: $color-green600;
        background-color: #fff;
        border: none;
        outline: none;

        &:hover {
          background-color: #fff;
          text-decoration: underline;
          border: none;
        }
      }
    }

    .loading-container {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-left: auto;

      .loading-message {
        color: $color-green400;
        flex-shrink: 0;
      }

      .progress-wrapper {
        width: 180px;
        .progress-bar {
          width: 100%;
          height: 8px;
          background: $color-gray400;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-bar-fill {
          width: v-bind(progressBarWidth);
          height: 100%;
          background: $color-green600;
          border-radius: 4px;

          &.done {
            transition: width 0.3s ease-out;
          }
        }
      }
    }
  }

  .saved-link {
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    .saved-link-icon {
      color: $color-green600;
    }
  }

  .full-bleed {
    width: auto;
  }
}

@keyframes ideaGlow {
  0% {
    transform: scale(1);
    filter: drop-shadow(0 0 0 rgba(0, 145, 127, 0));
    opacity: 0.92;
  }
  58% {
    transform: scale(1);
    filter: drop-shadow(0 0 0 rgba(0, 145, 127, 0));
    opacity: 0.92;
  }
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0 rgba(0, 145, 127, 0));
    opacity: 0.92;
  }
  70% {
    transform: scale(1.12);
    filter: drop-shadow(0 0 6px rgba(0, 145, 127, 0.6));
    opacity: 1;
  }
  84% {
    transform: scale(1.02);
    filter: drop-shadow(0 0 3px rgba(0, 145, 127, 0.3));
    opacity: 1;
  }
}
</style>
